import {
  getCustomerRepositoryListQueryKey,
  useCustomerRepositoryDestroy,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { LoaderCircleIcon, XIcon } from "lucide-react"

import {
  useDocuments,
  type CommonDocument,
} from "@/components/documents/context/DocumentsProvider"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"

export interface DeleteDialogProps {
  className?: string
  doc: CommonDocument
}

export function DeleteDocumentDialog({ className, doc }: DeleteDialogProps) {
  const queryClient = useQueryClient()
  const routeApi = getRouteApi("/_dashboard/documents")
  const { companyUuid } = routeApi.useRouteContext()

  const { allDocuments } = useDocuments()

  const { mutate: deleteDocument, isPending } = useCustomerRepositoryDestroy({
    mutation: {
      onSuccess: (_, variables) => {
        const queryKey = getCustomerRepositoryListQueryKey(companyUuid)
        const updatedList = allDocuments.filter(
          (doc) => doc.uuid !== variables.uuid,
        )
        queryClient.setQueryData(queryKey, updatedList)
      },
    },
  })

  if (isPending) {
    return (
      <LoaderCircleIcon
        className={cn("text-destructive inline-block animate-spin", className)}
      />
    )
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild className="cursor-pointer">
        <XIcon className={cn("text-destructive inline-block", className)} />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete file?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{" "}
            <span className="text-primary font-bold">
              {doc.isConfirmed ? doc.typeName : doc.filename}
            </span>
            ?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => deleteDocument({ companyUuid, uuid: doc.uuid })}
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
